import {
  TIMEZONE_OPTIONS,
  type CompanyAdminDto,
  type UpdateCompanyPayload,
} from '@performance-crm/shared';
import { Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { refreshSession } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  useAdminCatalogs,
  useAdminCompanies,
  useAdminProjects,
  useCreateCompany,
  useUpdateCompany,
} from '@/lib/queries';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const SEM: Array<[keyof CompanyAdminDto['settings']['semaphore'], string]> = [
  // La clave conserva su nombre histórico (P28); el umbral es el del % contacto efectivo del Embudo y el ranking.
  ['contactabilidad', '% contacto efectivo (Embudo y ranking)'],
  ['citasConcretadas', 'Citas concretadas'],
  ['visitasSobreCaptaciones', 'Visitas / captaciones'],
  ['separacionesSobreVisitas', 'Separaciones / visitas'],
  ['ventasSobreCaptaciones', 'Ventas / captaciones'],
];

/** Datos y configuración de la empresa (companySettingsSchema) con formulario, no JSON crudo. */
export function CompanyTab({ companyId }: { companyId: string }) {
  const { user } = useAuth();
  const { data: companies, isLoading } = useAdminCompanies();
  const company = companies?.find((c) => c.id === companyId);
  const { data: catalogs } = useAdminCatalogs(companyId);
  const { data: projects } = useAdminProjects(companyId);
  const update = useUpdateCompany();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const isAdmin = !!user?.memberships.some((m) => m.companyId === companyId && m.role === 'ADMIN');
  const [f, setF] = useState<{
    name: string;
    ruc: string;
    visible: boolean;
    sla: string;
    autoFirstTask: boolean;
    firstTaskType: 'LLAMADA' | 'WHATSAPP';
    blockDuplicates: boolean;
    start: string;
    end: string;
    days: number[];
    requirePriorSeparation: boolean;
    digital: string[];
    pauta: string[];
    portal: string[];
    technical: string;
    testPatterns: string;
    semaphore: CompanyAdminDto['settings']['semaphore'] | null;
    metaToken: string;
    metaPages: string;
    metaDefaultProject: string;
    metaForms: string;
    timezone: string;
    currency: 'USD' | 'PEN';
    penPerUsd: string;
    waPhones: string;
    waDefaultProject: string;
    botMaxAttempts: string;
    botWindowHours: string;
    botQuietFrom: string;
    botQuietTo: string;
    botResumeMinutes: string;
  }>({
    name: '',
    ruc: '',
    visible: true,
    sla: '15',
    autoFirstTask: true,
    firstTaskType: 'LLAMADA',
    blockDuplicates: false,
    start: '09:00',
    end: '19:00',
    days: [1, 2, 3, 4, 5, 6],
    requirePriorSeparation: true,
    digital: [],
    pauta: [],
    portal: [],
    technical: '',
    testPatterns: '',
    semaphore: null,
    metaToken: '',
    metaPages: '',
    metaDefaultProject: '',
    metaForms: '',
    timezone: 'America/Lima',
    currency: 'USD',
    penPerUsd: '3.75',
    waPhones: '',
    waDefaultProject: '',
    botMaxAttempts: '3',
    botWindowHours: '48',
    botQuietFrom: '22:00',
    botQuietTo: '08:00',
    botResumeMinutes: '30',
  });
  useEffect(() => {
    if (!company) return;
    const s = company.settings;
    setF({
      name: company.name,
      ruc: company.ruc ?? '',
      visible: company.active,
      sla: String(s.firstContactSlaMinutes),
      autoFirstTask: s.autoFirstTask,
      firstTaskType: s.firstTaskType,
      blockDuplicates: s.blockDuplicateManualLeads,
      start: s.businessHours.start,
      end: s.businessHours.end,
      days: s.businessHours.days,
      requirePriorSeparation: s.requirePriorSeparationForSale,
      digital: s.digitalCategories,
      pauta: s.pautaCategories,
      portal: s.portalCategories,
      technical: s.technicalDropReasons.join(', '),
      testPatterns: s.testEmailPatterns.join(', '),
      semaphore: s.semaphore,
      metaToken: s.metaIntegration?.pageAccessToken ?? '',
      metaPages: (s.metaIntegration?.pageIds ?? []).join(', '),
      metaDefaultProject: s.metaIntegration?.defaultProjectId ?? '',
      metaForms: Object.entries(s.metaIntegration?.formProjects ?? {})
        .map(([k, v]) => `${k}=${v}`)
        .join('\n'),
      timezone: s.timezone,
      currency: s.currency,
      penPerUsd: String(s.penPerUsd),
      waPhones: (s.whatsappIntegration?.phoneNumberIds ?? []).join(', '),
      waDefaultProject: s.whatsappIntegration?.defaultProjectId ?? '',
      botMaxAttempts: String(s.bot.maxAttempts),
      botWindowHours: String(s.bot.attemptWindowHours),
      botQuietFrom: s.bot.quietFrom ?? '',
      botQuietTo: s.bot.quietTo ?? '',
      botResumeMinutes: String(s.bot.resumeAfterMinutes),
    });
  }, [company]);
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));
  const toggle = (key: 'digital' | 'pauta' | 'portal' | 'days', v: string | number) =>
    set({
      [key]: (f[key] as Array<string | number>).includes(v)
        ? (f[key] as Array<string | number>).filter((x) => x !== v)
        : [...(f[key] as Array<string | number>), v],
    } as Partial<typeof f>);
  if (isLoading || !company) return <Skeleton className="h-64 w-full" />;
  const list = (s: string) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const formProjects: Record<string, string> = {};
    for (const line of f.metaForms.split('\n')) {
      const [k, v] = line.split('=').map((x) => x.trim());
      if (k && v) formProjects[k] = v;
    }
    const payload: UpdateCompanyPayload = {
      name: f.name,
      ruc: f.ruc || null,
      active: f.visible,
      settings: {
        firstContactSlaMinutes: Number(f.sla),
        autoFirstTask: f.autoFirstTask,
        firstTaskType: f.firstTaskType,
        blockDuplicateManualLeads: f.blockDuplicates,
        businessHours: { start: f.start, end: f.end, days: f.days },
        requirePriorSeparationForSale: f.requirePriorSeparation,
        digitalCategories: f.digital,
        pautaCategories: f.pauta,
        portalCategories: f.portal,
        technicalDropReasons: list(f.technical),
        testEmailPatterns: list(f.testPatterns).map((p) => p.toUpperCase()),
        ...(f.semaphore ? { semaphore: f.semaphore } : {}),
        metaIntegration: {
          pageAccessToken: f.metaToken || null,
          pageIds: list(f.metaPages),
          formProjects,
          defaultProjectId: f.metaDefaultProject || null,
        },
        timezone: f.timezone,
        currency: f.currency,
        penPerUsd: Number(f.penPerUsd) || 3.75,
        whatsappIntegration: {
          phoneNumberIds: list(f.waPhones),
          defaultProjectId: f.waDefaultProject || null,
        },
        bot: {
          maxAttempts: Number(f.botMaxAttempts) || 3,
          attemptWindowHours: Number(f.botWindowHours) || 48,
          quietFrom: f.botQuietFrom || null,
          quietTo: f.botQuietTo || null,
          resumeAfterMinutes: Number(f.botResumeMinutes) || 30,
        },
      },
    };
    try {
      await update.mutateAsync({ id: companyId, input: payload });
      // Si cambió la visibilidad, el selector de empresas debe enterarse (recarga la sesión).
      if (payload.active !== company.active) await refreshSession();
      toast.success(
        'Configuración guardada',
        payload.active === false
          ? 'La empresa quedó oculta: solo los administradores la ven aquí.'
          : undefined,
      );
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const cats = catalogs?.mediumCategories.filter((c) => c.active) ?? [];
  return (
    <form onSubmit={submit} className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {company.name}
            {isAdmin && (
              <Button type="button" size="sm" variant="outline" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" /> Nueva empresa
              </Button>
            )}
          </CardTitle>
          <CardDescription>
            {company.groupName} · {company.projects} proyectos · {company.users} membresías
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="c-name">Nombre</Label>
            <Input
              id="c-name"
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-ruc">RUC</Label>
            <Input id="c-ruc" value={f.ruc} onChange={(e) => set({ ruc: e.target.value })} />
          </div>
          {isAdmin && (
            <div className="space-y-1">
              <Label htmlFor="c-visible">Visibilidad</Label>
              <label
                htmlFor="c-visible"
                className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm"
              >
                <input
                  id="c-visible"
                  type="checkbox"
                  checked={f.visible}
                  onChange={(e) => set({ visible: e.target.checked })}
                  data-testid="company-visible"
                />
                {f.visible ? 'Visible para el equipo' : 'Oculta (solo administradores)'}
              </label>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="c-sla">Plazo de primer contacto (min)</Label>
            <Input
              id="c-sla"
              type="number"
              min={1}
              value={f.sla}
              onChange={(e) => set({ sla: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-block-dup">Personas repetidas al registrar a mano</Label>
            <label
              htmlFor="c-block-dup"
              className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm"
            >
              <input
                id="c-block-dup"
                type="checkbox"
                checked={f.blockDuplicates}
                onChange={(e) => set({ blockDuplicates: e.target.checked })}
                data-testid="company-block-duplicates"
              />
              {f.blockDuplicates
                ? 'Bloquear si ya tiene un lead activo'
                : 'Avisar y registrar como recontacto'}
            </label>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-first-task">Tarea automática al registrar un lead</Label>
            <div className="flex items-center gap-2">
              <label
                htmlFor="c-first-task"
                className="flex h-10 flex-1 items-center gap-2 rounded-md border px-3 text-sm"
              >
                <input
                  id="c-first-task"
                  type="checkbox"
                  checked={f.autoFirstTask}
                  onChange={(e) => set({ autoFirstTask: e.target.checked })}
                  data-testid="company-auto-first-task"
                />
                Crear «Primer contacto» (captación + plazo)
              </label>
              <Select
                aria-label="Tipo de la tarea automática"
                value={f.firstTaskType}
                onChange={(e) => set({ firstTaskType: e.target.value as 'LLAMADA' | 'WHATSAPP' })}
                disabled={!f.autoFirstTask}
              >
                <option value="LLAMADA">Llamada</option>
                <option value="WHATSAPP">WhatsApp</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-start">Horario comercial · inicio</Label>
            <Input
              id="c-start"
              type="time"
              value={f.start}
              onChange={(e) => set({ start: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-end">Horario comercial · fin</Label>
            <Input
              id="c-end"
              type="time"
              value={f.end}
              onChange={(e) => set({ end: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Días hábiles</Label>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggle('days', i)}
                  className={`rounded border px-2 py-1 text-xs ${f.days.includes(i) ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-tz">Zona horaria de negocio</Label>
            <Input
              id="c-tz"
              list="tz-list"
              value={f.timezone}
              onChange={(e) => set({ timezone: e.target.value })}
              required
            />
            <datalist id="tz-list">
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-currency">Moneda por defecto</Label>
            <select
              id="c-currency"
              value={f.currency}
              onChange={(e) => set({ currency: e.target.value as 'USD' | 'PEN' })}
              className="border-input bg-card flex h-10 w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="USD">US$</option>
              <option value="PEN">S/</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-fx">Tipo de cambio (soles por dólar)</Label>
            <Input
              id="c-fx"
              type="number"
              step="any"
              min={0.01}
              value={f.penPerUsd}
              onChange={(e) => set({ penPerUsd: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-3">
            <input
              type="checkbox"
              checked={f.requirePriorSeparation}
              onChange={(e) => set({ requirePriorSeparation: e.target.checked })}
              className="h-4 w-4"
            />
            Una venta requiere separación o proforma previa
          </label>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Reglas del embudo</CardTitle>
          <CardDescription>
            Qué categorías de medio cuentan como lead digital, pauta y portales; motivos técnicos
            que excluyen procesos; correos de prueba. Los medios de captación en sí (Facebook,
            Urbania, referidos…) se crean en Catálogos.
          </CardDescription>
          <Button asChild type="button" variant="outline" size="sm" className="mt-2 w-fit">
            <Link to={`/admin?tab=catalogos&companyId=${companyId}#medios`}>
              <Plus className="h-4 w-4" /> Agregar un medio de captación
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(
            [
              ['digital', 'Categorías digitales (LEADS)'],
              ['pauta', 'Pauta (LEADS_PAUTA)'],
              ['portal', 'Portales (LEADS_PORTALES)'],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label>{label}</Label>
              <div className="flex flex-wrap gap-1">
                {cats.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggle(key, c.code)}
                    className={`rounded border px-2 py-1 text-xs ${f[key].includes(c.code) ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
                  >
                    {c.code}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="c-tech">Motivos de caída técnicos (códigos, coma)</Label>
              <Input
                id="c-tech"
                value={f.technical}
                onChange={(e) => set({ technical: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="c-test">Patrones de correo de prueba (coma)</Label>
              <Input
                id="c-test"
                value={f.testPatterns}
                onChange={(e) => set({ testPatterns: e.target.value })}
              />
            </div>
          </div>
          {f.semaphore && (
            <div className="space-y-1">
              <Label>Semáforos (verde ≥ / ámbar ≥, en proporción 0–1)</Label>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {SEM.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 text-sm">
                    <span className="w-40 text-xs">{label}</span>
                    <Input
                      type="number"
                      step="any"
                      min={0}
                      max={1}
                      aria-label={`${label} verde`}
                      className="h-8 w-20"
                      value={f.semaphore![k].green}
                      onChange={(e) =>
                        set({
                          semaphore: {
                            ...f.semaphore!,
                            [k]: { ...f.semaphore![k], green: Number(e.target.value) },
                          },
                        })
                      }
                    />
                    <Input
                      type="number"
                      step="any"
                      min={0}
                      max={1}
                      aria-label={`${label} ámbar`}
                      className="h-8 w-20"
                      value={f.semaphore![k].amber}
                      onChange={(e) =>
                        set({
                          semaphore: {
                            ...f.semaphore!,
                            [k]: { ...f.semaphore![k], amber: Number(e.target.value) },
                          },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Meta Lead Ads</CardTitle>
          <CardDescription>
            Páginas que envían leads a esta empresa y a qué proyecto va cada formulario. El token de
            página permite completar leads que llegan solo con leadgen_id.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="c-pages">IDs de página (coma)</Label>
            <Input
              id="c-pages"
              value={f.metaPages}
              onChange={(e) => set({ metaPages: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-default">Proyecto por defecto</Label>
            <select
              id="c-default"
              value={f.metaDefaultProject}
              onChange={(e) => set({ metaDefaultProject: e.target.value })}
              className="border-input bg-card flex h-10 w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-token">Token de página</Label>
            <Input
              id="c-token"
              type="password"
              value={f.metaToken}
              onChange={(e) => set({ metaToken: e.target.value })}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-forms">
              Formulario → proyecto (form_id=projectId, uno por línea)
            </Label>
            <Textarea
              id="c-forms"
              rows={3}
              value={f.metaForms}
              onChange={(e) => set({ metaForms: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>WhatsApp Cloud API</CardTitle>
          <CardDescription>
            Números (phone_number_id) cuyos mensajes entran a esta empresa por{' '}
            <code>/api/v1/inbound/whatsapp/webhook</code>. Una persona nueva crea un lead en el
            proyecto por defecto; una conocida escribe en la conversación de su lead y el
            multiagente recibe <code>message.inbound</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="c-wa-phones">IDs de número (coma)</Label>
            <Input
              id="c-wa-phones"
              value={f.waPhones}
              onChange={(e) => set({ waPhones: e.target.value })}
              placeholder="1234567890"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-wa-project">Proyecto por defecto</Label>
            <select
              id="c-wa-project"
              value={f.waDefaultProject}
              onChange={(e) => set({ waDefaultProject: e.target.value })}
              className="border-input bg-card flex h-10 w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Bot de atención</CardTitle>
          <CardDescription>
            Límites del bot que escribe primero a los leads nuevos (se activa por proyecto en Equipo
            → Distribución). El bot los lee por la API; el plazo de retoma lo aplica el CRM.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="c-bot-attempts">Mensajes máximos sin respuesta</Label>
            <Input
              id="c-bot-attempts"
              type="number"
              min={1}
              max={20}
              value={f.botMaxAttempts}
              onChange={(e) => set({ botMaxAttempts: e.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              Después de estos intentos el lead pasa a "Sin respuesta".
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-bot-window">Repartidos en… (horas)</Label>
            <Input
              id="c-bot-window"
              type="number"
              min={1}
              max={720}
              value={f.botWindowHours}
              onChange={(e) => set({ botWindowHours: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-bot-resume">
              El bot retoma si el asesor no gestiona en… (minutos)
            </Label>
            <Input
              id="c-bot-resume"
              type="number"
              min={1}
              max={1440}
              value={f.botResumeMinutes}
              onChange={(e) => set({ botResumeMinutes: e.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              Cada gestión del asesor reinicia el contador.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-bot-quiet-from">Silencio nocturno desde</Label>
            <Input
              id="c-bot-quiet-from"
              type="time"
              value={f.botQuietFrom}
              onChange={(e) => set({ botQuietFrom: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="c-bot-quiet-to">Silencio nocturno hasta</Label>
            <Input
              id="c-bot-quiet-to"
              type="time"
              value={f.botQuietTo}
              onChange={(e) => set({ botQuietTo: e.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              Vacíos = el bot escribe a cualquier hora. No se limita al horario de oficina.
            </p>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button type="submit" disabled={update.isPending} data-testid="company-save">
          Guardar configuración
        </Button>
      </div>
      {creating && <NewCompanyDialog onClose={() => setCreating(false)} />}
    </form>
  );
}

function NewCompanyDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateCompany();
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [ruc, setRuc] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({ name, slug, ruc: ruc || null });
      toast.success(
        'Empresa creada',
        'Se cargaron los catálogos iniciales y tu usuario quedó como ADMIN.',
      );
      onClose();
    } catch (err) {
      toast.error('No se pudo crear', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Nueva empresa</DialogTitle>
            <DialogDescription>
              Se crea dentro de tu grupo inmobiliario con los catálogos por defecto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="nc-name">Nombre</Label>
            <Input
              id="nc-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[̀-ͯ]/g, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)/g, ''),
                );
              }}
              required
              minLength={2}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nc-slug">Identificador</Label>
            <Input
              id="nc-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              pattern="[a-z0-9-]+"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nc-ruc">RUC</Label>
            <Input id="nc-ruc" value={ruc} onChange={(e) => setRuc(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending}>
              Crear empresa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
